import React, { useEffect, useState } from "react";
import { Search, Save, StickyNote, IdCard, Trash2, X, UserRound, Plus, Upload, BellRing, Pencil, Archive, ChevronDown, ChevronUp, Check, Eraser, Mail, MessageSquare } from "lucide-react";
import api, { mediaSrc, avatarFor, ymd, hasShipped } from "@/lib/api";
import { toast } from "sonner";
import AvatarCropper from "@/components/AvatarCropper";
import ContactActions from "@/components/ContactActions";
import EditRecruitModal from "@/components/EditRecruitModal";
import { BroadcastComposer } from "@/components/Broadcast";

const input = "w-full rsp-card px-4 py-2.5 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)]";

function NotesPanel({ recruitId, onClose }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/me/recruits/${recruitId}/notes`).then((r) => setNotes(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [recruitId]);

  const saveAndClose = async () => {
    if (!text.trim()) { onClose(); return; }
    setSaving(true);
    try {
      await api.post(`/me/recruits/${recruitId}/notes`, { text, follow_up_date: followUp || null });
      toast.success("Note saved.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save note.");
    } finally { setSaving(false); }
  };
  const clear = () => { setText(""); setFollowUp(""); };
  const del = async (noteId) => {
    try { await api.delete(`/me/recruits/${recruitId}/notes/${noteId}`); load(); }
    catch (e) { toast.error("Could not delete."); }
  };

  return (
    <div className="mt-3 rounded-lg border border-[var(--rsp-border)] bg-[var(--rsp-bg-2)] p-4" data-testid="notes-panel">
      <div className="flex items-center justify-between mb-3">
        <span className="eyebrow" style={{ letterSpacing: "0.14em" }}>Recruiter Notes</span>
        <button onClick={onClose} data-testid="note-close-x" className="text-[var(--rsp-muted)] hover:text-[var(--rsp-cream)]"><X size={18} /></button>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type your note here…"
        rows={6}
        className="w-full rsp-card px-3 py-3 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)] text-sm leading-relaxed resize-y min-h-[140px]"
        data-testid="note-input"
        autoFocus
      />
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <label className="text-xs eyebrow text-[var(--rsp-muted)]">Follow-up</label>
        <input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} title="Follow-up date"
          className="rsp-card px-2 py-1.5 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)] text-sm" data-testid="note-followup" />
        <div className="flex-1" />
        <button onClick={clear} data-testid="note-clear"
          className="px-4 py-2 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)] flex items-center gap-1.5 text-sm">
          <Eraser size={15} /> Clear
        </button>
        <button onClick={saveAndClose} disabled={saving} data-testid="note-save"
          className="btn-gold px-4 py-2 flex items-center gap-1.5 disabled:opacity-60" title="Save & close">
          <Check size={16} /> {saving ? "Saving…" : "Save & Close"}
        </button>
      </div>

      {notes.length > 0 && (
        <div className="space-y-2 max-h-56 overflow-y-auto rsp-scroll mt-4 pt-3 border-t border-[var(--rsp-border)]">
          {notes.map((n) => (
            <div key={n.id} className="flex items-start justify-between gap-2 text-sm bg-[var(--rsp-card)] rounded p-2.5">
              <div>
                <div className="whitespace-pre-wrap break-words">{n.text}</div>
                <div className="text-[var(--rsp-muted)] text-xs mt-0.5">
                  {new Date(n.created_at).toLocaleString()}
                  {n.follow_up_date && <span className="gold ml-2">· Follow-up {ymd(n.follow_up_date)}</span>}
                </div>
              </div>
              <button onClick={() => del(n.id)} data-testid={`note-del-${n.id}`} className="text-[var(--rsp-muted)] hover:text-red-400 shrink-0"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ShipBadge({ iso, id }) {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  const shipped = d <= 0;
  const soon = !shipped && d <= 30;
  return (
    <span data-testid={`ship-badge-${id}`}
      className={`text-xs font-head uppercase px-2 py-0.5 rounded shrink-0 border ${shipped ? "text-emerald-400 border-emerald-400/40" : soon ? "orange border-[var(--rsp-border)]" : "gold border-[var(--rsp-border)]"}`}>
      {shipped ? "Has Shipped" : `${d} ${d === 1 ? "Day" : "Days"}`}
    </span>
  );
}

function RecruitRow({ r, onCac, onDelete, onDelDemo, onEditDemo, onEdit }) {
  const [openNotes, setOpenNotes] = useState(false);
  const id = r.user_id;
  return (
    <div className="rsp-card p-3" data-testid={`recruit-row-${id}`}>
      <div className="flex items-center gap-3">
        <img src={r.photo_url ? mediaSrc(r.photo_url) : avatarFor(r)} alt={r.name}
          className="w-10 h-10 rounded-lg object-cover border border-[var(--rsp-border)]" data-testid={`recruit-avatar-${id}`} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{r.name}{r.is_demo && <span className="ml-2 text-xs orange">SAMPLE</span>}</div>
          <div className="text-[var(--rsp-gray)] text-sm truncate">{r.mos ? `${r.mos} · ` : ""}{r.rank || r.email}</div>
        </div>
        {!r.is_demo && <ShipBadge iso={r.ship_date} id={id} />}
        {!r.is_demo && (
          <label className="flex items-center gap-1 text-xs font-head uppercase cursor-pointer" title="CAC issued">
            <IdCard size={16} className={r.has_cac ? "text-emerald-400" : "text-[var(--rsp-muted)]"} />
            <input type="checkbox" checked={!!r.has_cac} onChange={(e) => onCac(id, e.target.checked)} data-testid={`cac-toggle-${id}`} />
          </label>
        )}
      </div>
      {!r.is_demo && (
        <div className="mt-2 space-y-1.5 text-sm">
          <div className="flex items-center gap-2">
            <span className="eyebrow shrink-0" style={{ letterSpacing: "0.12em" }}>Ship Date</span>
            <span className="text-[var(--rsp-cream)]" data-testid={`ship-date-${id}`}>
              {r.ship_date ? ymd(r.ship_date) : "—"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="eyebrow shrink-0" style={{ letterSpacing: "0.12em" }}>AIT End Date</span>
            <span className="text-[var(--rsp-cream)]" data-testid={`ait-date-${id}`}>{r.ait_end_date ? ymd(r.ait_end_date) : "—"}</span>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-2">
        {!r.is_demo && (
          <ContactActions phone={r.phone} email={r.contact_email || r.email} name={r.name} />
        )}
        {!r.is_demo && (
          <button onClick={() => setOpenNotes((v) => !v)} data-testid={`notes-btn-${id}`}
            className="text-sm px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)] flex items-center gap-1">
            <StickyNote size={14} /> Notes
          </button>
        )}
        {!r.is_demo && onEdit && (
          <button onClick={() => onEdit(r)} data-testid={`edit-recruit-btn-${id}`}
            className="text-sm px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-gold)] hover:border-[var(--rsp-gold)]/60 flex items-center gap-1 transition-colors">
            <Pencil size={14} /> Edit
          </button>
        )}
        {!r.is_demo && onDelete && (
          <button onClick={() => onDelete(r)} data-testid={`del-recruit-${id}`}
            className="text-sm px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-red-400 hover:border-red-400/60 flex items-center gap-1 transition-colors">
            <Trash2 size={14} /> Delete
          </button>
        )}
        {r.is_demo && (
          <>
            <button onClick={() => onEditDemo(r)} data-testid={`edit-demo-${id}`}
              className="text-sm px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)]">Edit sample</button>
            <button onClick={() => onDelDemo(id)} data-testid={`del-demo-${id}`}
              className="text-sm px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-red-400 flex items-center gap-1"><Trash2 size={14} /> Remove</button>
          </>
        )}
      </div>
      {openNotes && <NotesPanel recruitId={id} onClose={() => setOpenNotes(false)} />}
    </div>
  );
}

// The recruiter's OWN editable profile card (shown on the Profile tab).
export function RecruiterProfileCard() {
  const [profile, setProfile] = useState(null);
  const [cropFile, setCropFile] = useState(null);

  useEffect(() => { api.get("/me/recruiter-profile").then((r) => setProfile(r.data)).catch(() => {}); }, []);

  const saveProfile = async () => {
    try { const { data } = await api.put("/me/recruiter-profile", profile); setProfile(data); toast.success("Recruiter profile saved."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
  };
  const uploadPhoto = async (blob) => {
    setCropFile(null);
    try {
      const fd = new FormData(); fd.append("file", blob, "avatar.jpg");
      const { data } = await api.post("/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: saved } = await api.put("/me/recruiter-profile", { ...profile, photo_url: data.url });
      setProfile(saved);
      toast.success("Photo updated.");
    } catch (err) { toast.error("Upload failed."); }
  };
  const setP = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  if (!profile) return null;
  return (
    <div className="rsp-card p-4 space-y-3" data-testid="recruiter-profile-card">
      <div className="font-head uppercase tracking-wide">My Recruiter Profile</div>
      <input className={input} value={profile.name || ""} onChange={(e) => setP("name", e.target.value)} placeholder="Name" data-testid="rec-name" />
      <div className="grid grid-cols-2 gap-3">
        <input className={input} value={profile.phone || ""} onChange={(e) => setP("phone", e.target.value)} placeholder="Phone" data-testid="rec-phone" />
        <input className={input} value={profile.email || ""} onChange={(e) => setP("email", e.target.value)} placeholder="Login email (Google)" data-testid="rec-email" />
      </div>
      <div>
        <input className={input} value={profile.secondary_email || ""} onChange={(e) => setP("secondary_email", e.target.value)} placeholder="Secondary contact email (recruits email this)" data-testid="rec-secondary-email" />
        <div className="text-[var(--rsp-muted)] text-xs mt-1">Recruits use this address when they tap Email. It won't change your login email.</div>
      </div>
      <div className="flex items-center gap-3">
        {profile.photo_url ? (
          <img src={mediaSrc(profile.photo_url)} alt="avatar" className="w-14 h-14 rounded-lg object-cover border border-[var(--rsp-border)]" />
        ) : (
          <img src={avatarFor(profile)} alt="avatar" className="w-14 h-14 rounded-lg object-cover border border-[var(--rsp-border)]" data-testid="recruiter-avatar" />
        )}
        <label className="btn-gold px-4 py-2.5 cursor-pointer text-sm flex items-center gap-2" data-testid="rec-photo-upload-label">
          <Upload size={15} />
          <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) setCropFile(e.target.files[0]); e.target.value = ""; }} data-testid="rec-photo-upload" />
          Upload photo
        </label>
      </div>
      {cropFile && <AvatarCropper file={cropFile} onCancel={() => setCropFile(null)} onConfirm={uploadPhoto} />}
      <input className={input} value={profile.url || ""} onChange={(e) => setP("url", e.target.value)} placeholder="Website URL" data-testid="rec-url" />
      <textarea className={input} rows={2} value={profile.bio || ""} onChange={(e) => setP("bio", e.target.value)} placeholder="Bio" data-testid="rec-bio" />
      <button onClick={saveProfile} className="btn-gold w-full py-2.5 flex items-center justify-center gap-2" data-testid="rec-save"><Save size={16} /> Save</button>
    </div>
  );
}

// The recruiter's roster + broadcast composer (shown on the Recruits tab).
export function RecruiterRoster() {
  const [recruits, setRecruits] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [q, setQ] = useState("");
  const [editDemo, setEditDemo] = useState(null);
  const [editRecruit, setEditRecruit] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [showShipped, setShowShipped] = useState(false);

  const load = () => api.get("/me/recruits").then((r) => setRecruits(r.data)).catch(() => {});
  const loadFollowups = () => api.get("/me/followups").then((r) => setFollowups(r.data)).catch(() => {});
  useEffect(() => { load(); loadFollowups(); }, []);

  const setCac = async (id, val) => {
    try { await api.put(`/me/recruits/${id}/cac`, { has_cac: val }); load(); }
    catch (e) { toast.error("Could not update CAC."); }
  };
  const doDelete = async () => {
    const r = confirmDel; setConfirmDel(null);
    try { await api.delete(`/me/recruits/${r.user_id}`); load(); toast.success(`${r.name} deleted.`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete recruit."); }
  };
  const delDemo = async (id) => {
    try { await api.delete(`/me/recruits/demo/${id}`); load(); } catch (e) { toast.error("Could not remove."); }
  };
  const saveDemo = async () => {
    try {
      await api.put(`/me/recruits/demo/${editDemo.user_id}`, {
        name: editDemo.name, email: editDemo.email, mos: editDemo.mos, rank: editDemo.rank,
        ship_date: editDemo.ship_date, bct_location: editDemo.bct_location, phone: editDemo.phone, contact_email: editDemo.contact_email,
      });
      setEditDemo(null); load(); toast.success("Sample updated.");
    } catch (e) { toast.error("Could not update."); }
  };

  const all = recruits.filter((r) => (r.name || r.email || "").toLowerCase().includes(q.toLowerCase()));
  const active = all.filter((r) => r.is_demo || !hasShipped(r.ship_date));
  const shipped = all.filter((r) => !r.is_demo && hasShipped(r.ship_date));
  // Mass contact targets = real recruits who have NOT shipped yet.
  const notShipped = recruits.filter((r) => !r.is_demo && !hasShipped(r.ship_date));
  const emails = [...new Set(notShipped.map((r) => (r.contact_email || r.email || "").trim()).filter((e) => e && !e.endsWith("@import.rspriverside.local")))];
  const phones = [...new Set(notShipped.map((r) => (r.phone || "").trim()).filter(Boolean))];
  const emailAll = () => {
    if (!emails.length) return toast.error("No recruit emails available.");
    window.location.href = `mailto:?bcc=${encodeURIComponent(emails.join(","))}`;
  };
  const textAll = () => {
    if (!phones.length) return toast.error("No recruit phone numbers available.");
    window.location.href = `sms:${phones.join(",")}`;
  };
  const setE = (k, v) => setEditDemo((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-6" data-testid="recruiter-roster">
      <BroadcastComposer />
      {followups.length > 0 && (
        <div className="rsp-card p-4 border-l-4" style={{ borderLeftColor: "var(--rsp-gold)" }} data-testid="followups-due">
          <div className="font-head uppercase tracking-wide flex items-center gap-2 mb-2"><BellRing size={18} className="gold" /> Follow-Ups Due · {followups.length}</div>
          <div className="space-y-2">
            {followups.map((f) => (
              <div key={f.note_id} className="bg-[var(--rsp-bg-2)] rounded p-2.5 text-sm" data-testid={`followup-${f.note_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-[var(--rsp-cream)]">{f.recruit_name}</span>
                  <span className="text-[var(--rsp-muted)] text-xs">Due {ymd(f.follow_up_date)}</span>
                </div>
                <div className="text-[var(--rsp-gray)] mt-0.5">{f.text}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <span className="eyebrow" style={{ letterSpacing: "0.16em" }}>My Recruits · {active.length}</span>
          <div className="flex gap-2">
            <button onClick={emailAll} data-testid="email-all-btn" title="Email all recruits who haven't shipped (opens your email app)"
              className="text-xs px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-gold)] hover:border-[var(--rsp-gold)]/60 flex items-center gap-1.5 transition-colors">
              <Mail size={14} /> Email All{emails.length ? ` (${emails.length})` : ""}
            </button>
            <button onClick={textAll} data-testid="text-all-btn" title="Text all recruits who haven't shipped (opens your messaging app)"
              className="text-xs px-3 py-1.5 rounded border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-gold)] hover:border-[var(--rsp-gold)]/60 flex items-center gap-1.5 transition-colors">
              <MessageSquare size={14} /> Text All{phones.length ? ` (${phones.length})` : ""}
            </button>
          </div>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rsp-muted)]" size={16} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recruits…" className={`${input} pl-9`} data-testid="recruit-search" />
        </div>
        <div className="space-y-2">
          {active.map((r) => (
            <RecruitRow key={r.user_id} r={r} onCac={setCac} onDelete={setConfirmDel} onDelDemo={delDemo} onEditDemo={setEditDemo} onEdit={setEditRecruit} />
          ))}
          {active.length === 0 && shipped.length === 0 && <p className="text-[var(--rsp-muted)]">No recruits assigned yet.</p>}
          {active.length === 0 && shipped.length > 0 && <p className="text-[var(--rsp-muted)] text-sm">No active recruits — all have shipped. See the archived list below.</p>}
        </div>

        {shipped.length > 0 && (
          <div className="mt-4">
            <button onClick={() => setShowShipped((v) => !v)} data-testid="toggle-shipped"
              className="w-full flex items-center justify-between rsp-card px-4 py-2.5 text-sm text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)]">
              <span className="flex items-center gap-2 eyebrow" style={{ letterSpacing: "0.14em" }}><Archive size={14} /> Shipped · Archived · {shipped.length}</span>
              {showShipped ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {showShipped && (
              <div className="space-y-2 mt-2 opacity-90" data-testid="shipped-list">
                {shipped.map((r) => (
                  <RecruitRow key={r.user_id} r={r} onCac={setCac} onDelete={setConfirmDel} onDelDemo={delDemo} onEditDemo={setEditDemo} onEdit={setEditRecruit} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {confirmDel && <DeleteConfirm name={confirmDel.name} onCancel={() => setConfirmDel(null)} onConfirm={doDelete} />}

      {editRecruit && <EditRecruitModal recruit={editRecruit} onClose={() => setEditRecruit(null)} onSaved={load} />}

      {editDemo && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setEditDemo(null)}>
          <div className="rsp-card p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()} data-testid="demo-edit-modal">
            <div className="font-head uppercase tracking-wide text-lg">Edit Sample Recruit</div>
            <input className={input} value={editDemo.name || ""} onChange={(e) => setE("name", e.target.value)} placeholder="Name" />
            <div className="grid grid-cols-2 gap-2">
              <input className={input} value={editDemo.rank || ""} onChange={(e) => setE("rank", e.target.value)} placeholder="Rank" />
              <input className={input} value={editDemo.mos || ""} onChange={(e) => setE("mos", e.target.value)} placeholder="MOS" />
            </div>
            <input className={input} value={editDemo.bct_location || ""} onChange={(e) => setE("bct_location", e.target.value)} placeholder="BCT Location" />
            <div className="flex gap-2">
              <button onClick={() => setEditDemo(null)} className="flex-1 py-2 rounded-lg border border-[var(--rsp-border)] text-[var(--rsp-gray)]">Cancel</button>
              <button onClick={saveDemo} className="btn-gold flex-1 py-2" data-testid="demo-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Shared confirmation dialog for deleting a recruit.
export function DeleteConfirm({ name, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="rsp-card p-5 w-full max-w-sm space-y-4" onClick={(e) => e.stopPropagation()} data-testid="delete-recruit-modal">
        <div className="flex items-center gap-2 text-red-400"><Trash2 size={20} /><span className="font-head uppercase tracking-wide text-lg">Delete Recruit</span></div>
        <p className="text-[var(--rsp-gray)] text-sm">Permanently delete <span className="text-[var(--rsp-cream)] font-semibold">{name}</span> and all their notes? This cannot be undone.</p>
        <div className="flex gap-2">
          <button onClick={onCancel} className="flex-1 py-2 rounded-lg border border-[var(--rsp-border)] text-[var(--rsp-gray)]" data-testid="delete-cancel">Cancel</button>
          <button onClick={onConfirm} className="flex-1 py-2 rounded-lg bg-red-500/90 hover:bg-red-500 text-white font-head uppercase tracking-wide" data-testid="delete-confirm">Delete</button>
        </div>
      </div>
    </div>
  );
}
